import { useEffect, useState } from "react";
import { putMemberLevelsForTenant } from "../../../../api/memberLevels";
import { reportError, showUserNotice } from "../../../../api/errors";
import { useAdminData } from "../../AdminDataContext";
import { memberLevelStore, COLOR_PRESETS, type MemberLevel } from "../../utils/memberLevelStore";
import { benefitsStore, CATEGORY_LABELS, type ExclusiveBenefit } from "../../utils/benefitsStore";

interface FormState {
  name: string;
  description: string;
  className: string;
  minPoints: number;
  benefitIds: string[];
  annualFee: number;
  ageUnlimited: boolean;
  ageMin: number;
  ageMax: number;
}

const emptyForm: FormState = {
  name: "",
  description: "",
  className: "bg-gray-100 text-gray-600",
  minPoints: 0,
  benefitIds: [],
  annualFee: 98,
  ageUnlimited: true,
  ageMin: 18,
  ageMax: 65,
};

export default function MemberLevelConfig() {
  const { tenantLegacyId, reload } = useAdminData();
  const [levels, setLevels] = useState<MemberLevel[]>(() => memberLevelStore.getAll());
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [, setBenefitsVer] = useState(0);

  useEffect(() => benefitsStore.subscribe(() => setBenefitsVer((v) => v + 1)), []);

  const allBenefits: ExclusiveBenefit[] = benefitsStore.getAll();

  const refresh = () => setLevels(memberLevelStore.getAll());

  const persistLevels = async (): Promise<boolean> => {
    const list = memberLevelStore.getAll();
    if (list.length === 0) {
      showUserNotice("至少保留一个会员等级。");
      return false;
    }
    try {
      const next = await putMemberLevelsForTenant(tenantLegacyId, list);
      memberLevelStore.replaceFromApi(next);
      refresh();
      void reload({ silent: true });
      return true;
    } catch (e) {
      reportError(e);
      void reload({ silent: true });
      return false;
    }
  };

  const openAdd = () => { setForm(emptyForm); setEditingId("new"); };
  const openEdit = (l: MemberLevel) => {
    const hasRange = l.ageMinInclusive != null && l.ageMaxInclusive != null;
    setForm({
      name: l.name,
      description: l.description,
      className: l.className,
      minPoints: l.minPoints,
      benefitIds: l.benefitIds ?? [],
      annualFee: l.annualFee ?? 98,
      ageUnlimited: !hasRange,
      ageMin: hasRange ? (l.ageMinInclusive as number) : 18,
      ageMax: hasRange ? (l.ageMaxInclusive as number) : 65,
    });
    setEditingId(l.id);
  };
  const closeForm = () => { setEditingId(null); setForm(emptyForm); };

  const toggleBenefit = (id: string) => {
    setForm((p) => ({
      ...p,
      benefitIds: p.benefitIds.includes(id) ? p.benefitIds.filter((b) => b !== id) : [...p.benefitIds, id],
    }));
  };

  const handleSave = () => {
    void (async () => {
      if (!form.name.trim()) return;
      let ageMinInclusive: number | null = null;
      let ageMaxInclusive: number | null = null;
      if (!form.ageUnlimited) {
        const a = Math.max(0, Math.min(150, Math.floor(Number(form.ageMin))));
        const b = Math.max(0, Math.min(150, Math.floor(Number(form.ageMax))));
        if (a > b) {
          showUserNotice("年龄下限不能大于上限。");
          return;
        }
        ageMinInclusive = a;
        ageMaxInclusive = b;
      }
      const levelPayload = {
        name: form.name.trim(),
        description: form.description,
        className: form.className,
        minPoints: form.minPoints,
        benefitIds: form.benefitIds,
        annualFee: form.annualFee,
        ageMinInclusive,
        ageMaxInclusive,
      };
      if (editingId === "new") {
        memberLevelStore.add({ ...levelPayload, isSystem: false });
      } else if (editingId) {
        memberLevelStore.update(editingId, levelPayload);
      }
      refresh();
      if (await persistLevels()) closeForm();
    })();
  };

  const handleDelete = (id: string) => {
    void (async () => {
      if (memberLevelStore.getAll().length <= 1) {
        showUserNotice("至少保留一个会员等级。");
        setDeleteId(null);
        return;
      }
      memberLevelStore.remove(id);
      refresh();
      setDeleteId(null);
      await persistLevels();
    })();
  };

  const handleMove = (id: string, dir: "up" | "down") => {
    if (dir === "up") memberLevelStore.moveUp(id);
    else memberLevelStore.moveDown(id);
    refresh();
    void persistLevels();
  };

  // Group benefits by category
  const benefitsByCategory = allBenefits.reduce<Record<string, ExclusiveBenefit[]>>((acc, b) => {
    if (!acc[b.category]) acc[b.category] = [];
    acc[b.category].push(b);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-gray-900">会员等级字典</h3>
          <p className="text-xs text-gray-400 mt-0.5">定义协会的会员等级体系，并为每个等级配置专属权益</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i> 新增等级
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 flex gap-3">
        <i className="ri-information-line text-amber-500 text-base flex-shrink-0 mt-0.5"></i>
        <div className="text-xs text-amber-700 space-y-1">
          <p className="font-medium">使用说明</p>
          <p>• 会员等级由管理员在审核时手动分配，等级不与积分自动挂钩</p>
          <p>• 编辑等级时可从「专属权益字典」中勾选该等级享有的权益条目</p>
          <p>• 参考积分门槛仅供管理员参考，不自动影响会员等级评定</p>
        </div>
      </div>

      <div className="space-y-3">
        {levels.map((level, idx) => {
          const levelBenefits = benefitsStore.getByIds(level.benefitIds ?? []);
          return (
            <div key={level.id} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button onClick={() => handleMove(level.id, "up")} disabled={idx === 0} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-600 cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors">
                  <i className="ri-arrow-up-s-line text-sm"></i>
                </button>
                <button onClick={() => handleMove(level.id, "down")} disabled={idx === levels.length - 1} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-600 cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors">
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
              </div>

              <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-full text-xs font-bold text-gray-400 flex-shrink-0">{idx + 1}</div>
              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 ${level.className}`}>{level.name}</span>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-gray-800">{level.name}</span>
                  {level.isSystem && <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">系统</span>}
                </div>
                <p className="text-xs text-gray-400 truncate">{level.description}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {level.ageMinInclusive != null && level.ageMaxInclusive != null
                    ? `年龄：${level.ageMinInclusive}–${level.ageMaxInclusive} 周岁（含）`
                    : "年龄：不限"}
                </p>
                {levelBenefits.length > 0 && (
                  <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                    {levelBenefits.slice(0, 3).map((b) => (
                      <span key={b.id} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full whitespace-nowrap">
                        {b.name}
                      </span>
                    ))}
                    {levelBenefits.length > 3 && (
                      <span className="text-[10px] text-gray-400">+{levelBenefits.length - 3}项</span>
                    )}
                  </div>
                )}
              </div>

              <div className="text-right flex-shrink-0 space-y-1">
                <div>
                  <p className="text-xs text-gray-400">年费金额</p>
                  <p className="text-sm font-bold text-red-600">¥{(level.annualFee ?? 0).toFixed(0)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">参考积分</p>
                  <p className="text-xs font-semibold text-gray-500">{level.minPoints.toLocaleString()}</p>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => openEdit(level)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 cursor-pointer rounded-lg hover:bg-amber-50 transition-colors" title="编辑">
                  <i className="ri-edit-line text-sm"></i>
                </button>
                {!level.isSystem ? (
                  <button onClick={() => setDeleteId(level.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer rounded-lg hover:bg-red-50 transition-colors" title="删除">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                ) : (
                  <div className="w-8 h-8" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit Modal */}
      {editingId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[560px] p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800">{editingId === "new" ? "新增会员等级" : "编辑会员等级"}</h3>
              <button onClick={closeForm} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* Preview */}
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                <span className="text-xs text-gray-500">预览：</span>
                <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${form.className}`}>{form.name || "等级名称"}</span>
                <span className="text-xs text-gray-400">已选 {form.benefitIds.length} 项权益</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">等级名称 *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="如：高级会员" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" autoFocus />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">年费金额（元）</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">¥</span>
                    <input type="number" min={0} step={0.01} value={form.annualFee} onChange={(e) => setForm((p) => ({ ...p, annualFee: Math.max(0, Number(e.target.value)) }))} className="w-full border border-gray-200 rounded-xl pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">等级描述</label>
                  <input type="text" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="简短描述此等级的特征" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">参考积分门槛</label>
                  <input type="number" min={0} value={form.minPoints} onChange={(e) => setForm((p) => ({ ...p, minPoints: Math.max(0, Number(e.target.value)) }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">入会年龄限制</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, ageUnlimited: true }))}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold border-2 cursor-pointer transition-all ${form.ageUnlimited ? "border-gray-800 bg-gray-50" : "border-transparent bg-gray-100 text-gray-600 hover:border-gray-300"}`}
                  >
                    不限
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, ageUnlimited: false }))}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold border-2 cursor-pointer transition-all ${!form.ageUnlimited ? "border-gray-800 bg-gray-50" : "border-transparent bg-gray-100 text-gray-600 hover:border-gray-300"}`}
                  >
                    年龄范围（周岁，含）
                  </button>
                </div>
                {!form.ageUnlimited ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">下限</label>
                      <input
                        type="number"
                        min={0}
                        max={150}
                        value={form.ageMin}
                        onChange={(e) => setForm((p) => ({ ...p, ageMin: Math.max(0, Math.min(150, Number(e.target.value))) }))}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">上限</label>
                      <input
                        type="number"
                        min={0}
                        max={150}
                        value={form.ageMax}
                        onChange={(e) => setForm((p) => ({ ...p, ageMax: Math.max(0, Math.min(150, Number(e.target.value))) }))}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                      />
                    </div>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">徽章颜色</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((preset) => (
                    <button key={preset.className} onClick={() => setForm((p) => ({ ...p, className: preset.className }))} className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer transition-all text-xs font-medium ${form.className === preset.className ? "border-gray-800 shadow-sm" : "border-transparent hover:border-gray-300"} ${preset.className}`}>
                      {form.className === preset.className && <i className="ri-check-line text-xs"></i>}
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Benefits Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                    <i className="ri-gift-2-line text-red-500"></i>
                    专属权益配置
                    <span className="text-gray-400 font-normal">（已选 {form.benefitIds.length} 项）</span>
                  </label>
                  {form.benefitIds.length > 0 && (
                    <button onClick={() => setForm((p) => ({ ...p, benefitIds: [] }))} className="text-[10px] text-red-500 cursor-pointer hover:underline">
                      清空选择
                    </button>
                  )}
                </div>
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  {(Object.keys(benefitsByCategory) as string[]).map((cat) => (
                    <div key={cat} className="border-b border-gray-100 last:border-b-0">
                      <div className="px-3 py-2 bg-gray-50 text-[11px] font-semibold text-gray-500">
                        {CATEGORY_LABELS[cat as keyof typeof CATEGORY_LABELS]}类
                      </div>
                      <div className="p-2 grid grid-cols-2 gap-1">
                        {benefitsByCategory[cat].map((b) => {
                          const checked = form.benefitIds.includes(b.id);
                          return (
                            <label key={b.id} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all ${checked ? 'bg-red-50 border border-red-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleBenefit(b.id)} className="accent-red-600 cursor-pointer w-3.5 h-3.5 flex-shrink-0" />
                              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                <i className={`${b.icon} text-sm ${checked ? 'text-red-500' : 'text-gray-400'}`}></i>
                              </div>
                              <span className={`text-xs leading-tight ${checked ? 'text-red-700 font-medium' : 'text-gray-600'}`}>{b.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-5 border-t border-gray-100">
              <button onClick={closeForm} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleSave} disabled={!form.name.trim()} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer disabled:opacity-50 whitespace-nowrap">
                {editingId === "new" ? "创建等级" : "保存修改"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">删除会员等级</h3>
            <p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">删除后该等级将从所有业务模块中移除，已分配此等级的会员可能显示异常。</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
